webpackJsonp([45],{

/***/ "./node_modules/json-loader/index.js!./.cache/json/advanced-1-api-modelling.json":
/***/ (function(module, exports) {

	module.exports = {
		"data": {
			"markdownRemark": {
				"html": "<h3>Introduction</h3>\n<p>How To Hypermedia models the API based on collections and is not specific to any microformat (eg HAL, Collection+JSON, SIREN, JSON-LD + hydra, UBER or ATOM). This chapter explains the names/objects used throughout the tutorials for reference. Understanding this will help with understanding the approach to forms and the diagram representation of the TODO domain. Note: the collection always needs to be serialised to or deserialised from the across-the-wire format that the client requests. Most implementations will require multiple mime types and thus this tutorial sees those types as orthogonal and can be dealted with via programming patterns (eg Representator/Strategy) within the runtime framework pipeline.</p>\n<p><img src=\"/hypermedia/advanced/api-modelling.png\" alt=\"api modelling\"></p>\n<h3>Overview of the Semantic Link model</h3>\n<p>The Semantic Link conventions on hypermedia revolve around different ways to build up resources as representations with link relations.</p>\n<ul>\n<li>A <strong>representation</strong> has <code>links</code> to other representations and the attributes of the representation itself.</li>\n</ul>\n<p>Representations are generally referred to as a singleton or collection resource. A collection has a number of items each of which in turn are a representation. In any representation the link relations can also refer to another representation.</p>\n<Instruction>\n<p>Structurally</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"  path=\"...todo-hypermedia/api\"  nocopy=\"false\"><code><span class=\"token keyword\">.</span>\n└── Representation\n    ├── Singleton\n    └── Collection\n            └── Items\n</code></pre>\n      </div>\n</Instruction>\n<h3>Concrete implementation of the Semantic Link model</h3>\n<p>The next step is that the implementation has a slightly different abstraction. Singleton representations are concrete LinkedRepresentations and Collection representations are FeedRepresentations where its items become LinkedRepresentations. These abstractions align with building the way the Web works.</p>\n<h4>Resources</h4>\n<ul>\n<li><strong>LinkedRepresentation:</strong> all representations have links (inherit <code>LinkedRepresentation</code>)</li>\n<li><strong>Singleton representation:</strong> is a LinkedRepresentation because it inherits from LinkedRepresentation and is its own first-class citizen</li>\n<li><strong>Collection representation:</strong> is a logical resource which is an instantiated FeedRepresentation with references to zero-to-many singleton resources</li>\n<li><strong>FeedRepresentation:</strong> is a sparsely populated collection representation, where each item is populated as a FeedItemRepresentation (note: collections can also be modelled with multiple link rels of the same name)</li>\n<li><strong>FeedItemRepresentation:</strong> minimal set of information to identify a resource, eg the location (URI) and title</li>\n</ul>\n<p><img src=\"/backend/aspnetcore-todo-api/modelling-representations.png\" alt=\"modelling representations\"></p>\n<h5>Links</h5>\n<ul>\n<li><strong>Links:</strong> a set of information to identify a representation and the nature of its relationship as a <code>link relation</code> or “vocabulary” (is a set of <code>WebLink</code>s)</li>\n<li><strong>Link Relation:</strong> a descriptive attribute that defines the type of link, or the relationship between the source and destination resources. These are known at design and come in at least two forms: IANA and custom (eg also Schema.org, Microformats, Dublin Core and Activity Streams). <em>IANA Link Relations</em> are a <a href=\"https://www.iana.org/assignments/link-relations/link-relations.xhtml#link-relations-1\">published set</a> of publicly, well-known link relations. <em>Custom Link Relations</em> are specific to the domain (ie todo) of the application to model singletons and collection representations (see next tutorial page)</li>\n</ul>\n<blockquote>\n<p>Note: modelling representations requires links and most formal hypermedia types model collections—this is independent of the media type that is send across the wire (aka the Strategy/Representor Pattern). Input and output serialisers are covered in a later chapter.</p>\n</blockquote>\n<Instruction>\n<p>In pseudo code:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-uml\"   nocopy=\"false\"><code>abstract class LinkedRepresentation {\n    Links : WebLink[]\n}\n\nclass WebLink {\n   Rel : string\n   HRef : string\n   Title : string?\n   Type : string?\n}\n\nclass FeedRepresentation {\n    Items : FeedItemRepresentation[]\n}\n\nclass FeedItemRepresentation  {\n    Id : string\n    Title : string\n    Published : DateTime?\n    Updated : DateTime?\n    Author : string?\n    Categories : string[]?\n}</code></pre>\n      </div>\n</Instruction>\n<h3>Diagrams: representating singletons, collections and links</h3>\n<p>This diagram is one way to view the domain at its highest level. It should indicate the main groupings domain objects and their custom link relations. It should show the navigation of link relations to move around collections. It tends to not have the IANA link relations because clutter this view. Developers will need this when reasoning about the domain. The next view below this is the API itself (or some static view of the API if need be—in practice, the static view currently practiced for endpoints and attributes falls by the wayside and becomes an unnecessary overhead). Currently, there is no tooling to extract this view from the code base but wouldn’t be hard.</p>\n<p>Look at the model below to understand that:</p>\n<ul>\n<li>Home singleton (root of the network of data)</li>\n<li><code>tags</code> link rel to a collection of <code>tag</code> representations in the items</li>\n<li><code>users</code> link rel to a collection of <code>user</code> representations in the items</li>\n<li><code>me</code> link rel to a specific <code>user</code> representation (in this case this is also a virtual resource redirecting to another user representation)</li>\n</ul>\n<p>This diagram can be read as, the root (which has a version number) links to two collections and also a virtual resource to an item in one of those collections.</p>\n<p><img src=\"/hypermedia/advanced/diagram-example.png\"></p>\n<h4>Diagram Key</h4>\n<ul>\n<li>yellow box: singleton representation</li>\n<li>line with circle: named collection representation based on the link relation where the joining representation is an item</li>\n<li>line only: singleton representation through the link relation</li>\n<li>dotted line: virtual resource through the link relation</li>\n</ul>\n<p><img src=\"/hypermedia/advanced/diagram-key.png\"></p>\n<Instruction>\n<p>The model written in <a href=\"http://plantuml.com/class-diagram\"><code>puml</code></a></p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"   nocopy=\"false\"><code>class home as <span class=\"token string\">\"Home/Root of the API\"</span> <span class=\"token operator\">&lt;&lt;</span>H, <span class=\"token comment\" spellcheck=\"true\">#00FF00) >></span>\nhome +--<span class=\"token operator\">|</span><span class=\"token operator\">></span> tag <span class=\"token keyword\">:</span> tags\nhome .--<span class=\"token operator\">|</span><span class=\"token operator\">></span> user <span class=\"token keyword\">:</span> me\nhome +--<span class=\"token operator\">|</span><span class=\"token operator\">></span> user <span class=\"token keyword\">:</span> <span class=\"token function\">users</span>\n</code></pre>\n      </div>\n</Instruction>",
				"fields": {
					"slug": "/advanced/1-api-modelling/"
				},
				"excerpt": "Introduction How To Hypermedia models the API based on collections and is not specific to any microformat (eg HAL, Collection+JSON, SIREN...",
				"frontmatter": {
					"title": "API Modelling",
					"videoId": null,
					"videoAuthor": null,
					"parent": "/Users/todd/src/howtohypermedia/content/hypermedia/advanced/1-api-modelling.md absPath of file",
					"question": "Which statement about hypermedia servers is not correct?",
					"answers": [
						"Modelling hypermedia on collections is not the only approach",
						"Modelling on collections can be separated from the across-the-wire microformat",
						"Collections are just sets of singleton resources",
						"None of the above"
					],
					"correctAnswer": 3,
					"description": "Learn about how collections and singletons are modelled in hypermedia",
					"pageTitle": "Modelling the API through collections"
				}
			},
			"mds": {
				"edges": [
					{
						"node": {
							"frontmatter": {
								"title": "Get started with your favorite technology",
								"description": null
							},
							"fields": {
								"slug": "/choose/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Update Collection Resource",
								"description": "Learn the code mechanics of a edit forms on a collection resource that uses different mime-types"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/11-update-collection/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "It was great having you!",
								"description": null
							},
							"fields": {
								"slug": "/success/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Update Singleton Resource",
								"description": "Learn the code mechanics of a edit forms on a singleton resource"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/10-update-singleton/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Introduction",
								"description": "Learn how to build a hypermedia API that works within the constraints of REST"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/0-introduction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Getting Started",
								"description": "Learn how to build a hypermedia API "
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/1-getting-started/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "",
								"description": null
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/2-modelling-representations/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Modelling Domain",
								"description": "Learn how to diagram the API textually and get also get visual output"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/3-modelling-domain-link-relations/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Representations",
								"description": "Learn the code mechanics of request/response of representation in an MVC framework with the root API representation containing links and a version"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/4-constructing-representations/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Collections",
								"description": "Learn the code mechanics of a singleton resource with the user resource"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/6-constructing-collection/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Searchable Resources",
								"description": "Learn the code mechanics of a search forms on a collection"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/8-constructing-search-resources/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Singletons",
								"description": "Learn the code mechanics of a singleton resource with the user resource"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/5-constructing-singleton/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Create Resource",
								"description": "Learn the code mechanics of a create forms on a collection"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/9-create-resource/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Virtual Resource",
								"description": "Learn the code mechanics of a virtual resource with the user resource"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/7-constructing-viritual/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Introduction",
								"description": "Learn how to build a todo app and admin functions over the top of a hypermedia API"
							},
							"fields": {
								"slug": "/vue-todo-app/0-introduction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Client-side URLs and routing",
								"description": "Design your application around the holding and reconstructing client state through the URI in the browser location requires matching client views and the data it requires"
							},
							"fields": {
								"slug": "/vue-todo-app/2-client-side-urls-and-routing/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Getting started",
								"description": "Learn how to build a todo client to use the hypermedia API "
							},
							"fields": {
								"slug": "/vue-todo-app/1-getting-started/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Client-side application cache",
								"description": "Client need to have one copy of representations in the application cache"
							},
							"fields": {
								"slug": "/vue-todo-app/3-application-cache/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Updates and deletes",
								"description": "The application cache also deals with the updates and deletion of resources"
							},
							"fields": {
								"slug": "/vue-todo-app/4-updates-and-deletes/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "On-demand Authentication",
								"description": "Be able to create and renew authentication tokens when the server requests valid authentication tokens"
							},
							"fields": {
								"slug": "/vue-todo-app/5-on-demand-authentication/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Summary",
								"description": "Learn how to build a todo app over the top of a hypermedia API"
							},
							"fields": {
								"slug": "/vue-todo-app/7-summary/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Getting Started",
								"description": "Learn how to build a general purpose client to browse over the API "
							},
							"fields": {
								"slug": "/vue-api-browser/1-getting-started/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Cloning Resources",
								"description": "Forms in hypermedia make cloning of trees straightforward and we can leverage that with drag and drop functionality alongside the client-side application cache"
							},
							"fields": {
								"slug": "/vue-todo-app/6-cloning-resources/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "HTML representation",
								"description": "Learn what and why to build HTML representation of data with code on demand"
							},
							"fields": {
								"slug": "/vue-api-browser/2-build-html-representation/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Introduction",
								"description": "Learn how to build a general purpose client to browse over the API "
							},
							"fields": {
								"slug": "/vue-api-browser/0-introduction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Generate HTML",
								"description": "Learn how to respond as HTML in ASP.NET Core"
							},
							"fields": {
								"slug": "/vue-api-browser/3-build-html-content-negotiation/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Register text/html",
								"description": "Learn how to respond to HTML content negotiation in ASP.NET Core"
							},
							"fields": {
								"slug": "/vue-api-browser/4-register-html-content-negotiation/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "GET application/json",
								"description": "Learn how to request JSON from the client"
							},
							"fields": {
								"slug": "/vue-api-browser/5-request-json/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Summary",
								"description": "Learn how to user hypermedia as engine to navigate an API and make changes"
							},
							"fields": {
								"slug": "/vue-api-browser/10-summary/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Displaying with interaction",
								"description": "Learn how to process JSON to make delete, create and update"
							},
							"fields": {
								"slug": "/vue-api-browser/6-displaying-with-interaction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Displaying forms",
								"description": "Learn how to process form values for create and edit forms"
							},
							"fields": {
								"slug": "/vue-api-browser/8-displaying-form-values/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Deleting a resource",
								"description": "Learn how to process JSON to try and delete a resource"
							},
							"fields": {
								"slug": "/vue-api-browser/7-try-delete-representation/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Introduction",
								"description": "Learn all about hypermedia and why a REST-style of architecture is simple and subtle. It's not really for the lazy or those who like to be clever."
							},
							"fields": {
								"slug": "/basics/0-introduction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Minimally layered design",
								"description": "Learn why hypermedia is a thin layer across persistence that should guide us through the business problem."
							},
							"fields": {
								"slug": "/basics/1-minimally-layered-design/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Submitting forms",
								"description": "Learn how to process creates (POST) and updates (PUT) on a resource"
							},
							"fields": {
								"slug": "/vue-api-browser/9-submitting-forms/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Big Picture (Trends)",
								"description": "Learn that distributed applications have trended towards data and business logic being processed at the client edge"
							},
							"fields": {
								"slug": "/basics/3-big-picture/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Hypermedia for the dedicated",
								"description": "Learn some techniques that will ease the server and client development"
							},
							"fields": {
								"slug": "/basics/4-for-the-dedicated/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Navigating a hypermedia API",
								"description": "Learn what a hypermedia looks like to be able to explore a domain."
							},
							"fields": {
								"slug": "/basics/2-navigating-api/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Multiple clients",
								"description": "Learn that most APIs should have multiple types of clients."
							},
							"fields": {
								"slug": "/advanced/0-multiple-clients/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "API Modelling",
								"description": "Learn about how collections and singletons are modelled in hypermedia"
							},
							"fields": {
								"slug": "/advanced/1-api-modelling/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Forms Modelling",
								"description": "Links and Forms are used to update resources and controlled by the server that the client then uses"
							},
							"fields": {
								"slug": "/advanced/2-forms-modelling/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Common Questions",
								"description": "Answer common questions about these tutorials"
							},
							"fields": {
								"slug": "/advanced/4-common-questions/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Todo Domain",
								"description": "Learn about domain of the API"
							},
							"fields": {
								"slug": "/advanced/3-todo-domain/"
							}
						}
					}
				]
			}
		},
		"pathContext": {
			"slug": "/advanced/1-api-modelling/"
		}
	};

/***/ })

});


// WEBPACK FOOTER //
// path---advanced-1-api-modelling-6f1a33ad53ff74e7865c.js